/**
 * The equipment screen: pick one lethal and one tactical.
 *
 * ## Why its own screen and its own file
 *
 * The loadout screen is a seventeen-hundred-line state machine built around a
 * weapon roster: a class rail, a cursor over weapon cells, two gun slots, a stat
 * sheet and a gunsmith hand-off. Equipment fits none of that — two flat lists, no
 * classes, no attachments, no per-item levelling — and bending the weapon screen
 * around it would mean threading a second kind of cell through every one of those
 * paths. So this is a sibling of the Gunsmith rather than a mode of the loadout:
 * opened from it, owns its own file, and cannot destabilise it.
 *
 * ## Unlocks
 *
 * Read live off `ctx.progression.level` every time the screen paints. A locked row
 * is shown, not hidden — the ladder is most of the reason to keep playing, and a
 * player who cannot see what is coming has nothing to climb toward.
 *
 * ## Persistence
 *
 * The choice is saved here rather than in `Equipment`, which is a simulation module
 * and should not own a storage key. It republishes on load through the same
 * `loadout:equipment` event the screen uses, so there is exactly one path into the
 * live system whether the choice came from storage or from a keypress.
 */

import { LETHAL, TACTICAL, EQUIPMENT, DEFAULT_LOADOUT } from '../game/equipment.data.js';
import { buildEquipmentModel } from '../weapons/models/equipment.js';
import { applyI18n, getLang, onLang, t } from './i18n.js';
import { commandBar } from './CommandBar.js';

const STORE_KEY = 'ob.equipment';

export const TUNING = {
  openKey: 'KeyT',
};

export class EquipmentScreen {
  constructor(ctx) {
    this.ctx = ctx;
    this.isOpen = false;
    this.column = 'lethal'; // which list the cursor is in
    this.cursor = { lethal: 0, tactical: 0 };
    this.selection = this._load();
    this.onClose = null;

    this._build();
    this._bind();

    // Follow the bus as well as drive it. Anything else that publishes a choice —
    // the dev panel, a future match rule, a saved class — would otherwise change the
    // simulation while this screen went on believing something different, and the
    // loadout chip reads from here. One event, one truth.
    ctx.events.on('loadout:equipment', (p) => {
      if (!p) return;
      let moved = false;
      for (const slot of ['lethal', 'tactical']) {
        if (p[slot] && EQUIPMENT[p[slot]] && this.selection[slot] !== p[slot]) {
          this.selection[slot] = p[slot];
          moved = true;
        }
      }
      if (!moved) return;
      this._save();
      if (this.isOpen) this._render();
    });

    // Publish once at startup so the simulation starts from the saved choice rather
    // than from its own defaults, which would silently differ from what this screen
    // shows the moment a player has ever changed it.
    this._publish();
  }

  /* -------------------------------- storage ------------------------------- */

  _load() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      const out = { ...DEFAULT_LOADOUT };
      if (raw?.lethal && EQUIPMENT[raw.lethal]) out.lethal = raw.lethal;
      if (raw?.tactical && EQUIPMENT[raw.tactical]) out.tactical = raw.tactical;
      return out;
    } catch {
      return { ...DEFAULT_LOADOUT };
    }
  }

  _save() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(this.selection));
    } catch {
      /* private browsing: the choice still applies for this session */
    }
  }

  _publish() {
    this.ctx.events.emit('loadout:equipment', { ...this.selection });
  }

  /* --------------------------------- level -------------------------------- */

  get level() {
    const p = this.ctx.progression;
    return p?.level ?? p?.model?.level ?? 1;
  }

  _unlocked(def) {
    return this.level >= def.unlock || !!this.ctx.progression?.debugUnlockAll;
  }

  /* -------------------------------- markup -------------------------------- */

  _build() {
    const root = document.createElement('div');
    root.className = 'eq-root';
    root.innerHTML = `
      <div class="eq-panel">
        <div class="eq-head">
          <div class="eq-title" data-i18n="eq.title"></div>
          <div class="eq-lv"><span data-i18n="eq.level"></span> <b class="eq-lv-n">1</b></div>
        </div>
        <div class="eq-body">
          <div class="eq-col" data-col="lethal">
            <div class="eq-colhead" data-i18n="eq.lethal"></div>
            <div class="eq-list"></div>
          </div>
          <div class="eq-col" data-col="tactical">
            <div class="eq-colhead" data-i18n="eq.tactical"></div>
            <div class="eq-list"></div>
          </div>
        </div>
        <div class="eq-detail"></div>
        <div class="eq-foot"></div>
      </div>`;
    (document.getElementById('ui-root') ?? document.body).appendChild(root);

    this.root = root;
    this.lvEl = root.querySelector('.eq-lv-n');
    this.lists = {
      lethal: root.querySelector('[data-col="lethal"] .eq-list'),
      tactical: root.querySelector('[data-col="tactical"] .eq-list'),
    };
    this.detailEl = root.querySelector('.eq-detail');
    this.footEl = root.querySelector('.eq-foot');
    applyI18n(root);
    this._offLang = onLang(() => {
      applyI18n(root);
      if (this.isOpen) this._render();
    });
  }

  _bind() {
    // Capture, like the other full-screen menus, so nothing leaks through to Input
    // and moves the player while they are reading a list.
    this._onKey = (e) => {
      if (!this.isOpen) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (this._handleKey(e.code, e.repeat)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('keydown', this._onKey, true);

    this.root.addEventListener('click', (e) => {
      const row = e.target.closest('[data-id]');
      if (row) {
        const def = EQUIPMENT[row.dataset.id];
        if (!def) return;
        this.column = def.slot ?? (LETHAL.includes(def) ? 'lethal' : 'tactical');
        this.cursor[this.column] = this._rosterFor(this.column).indexOf(def);
        this._confirm();
        return;
      }
      const col = e.target.closest('[data-col]');
      if (col) {
        this.column = col.dataset.col;
        this._render();
      }
    });
  }

  _rosterFor(col) {
    return col === 'tactical' ? TACTICAL : LETHAL;
  }

  /* --------------------------------- input -------------------------------- */

  _handleKey(code, repeat) {
    switch (code) {
      case 'ArrowUp':
      case 'KeyW': {
        const list = this._rosterFor(this.column);
        this.cursor[this.column] = (this.cursor[this.column] - 1 + list.length) % list.length;
        this._render();
        return true;
      }
      case 'ArrowDown':
      case 'KeyS': {
        const list = this._rosterFor(this.column);
        this.cursor[this.column] = (this.cursor[this.column] + 1) % list.length;
        this._render();
        return true;
      }
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'KeyA':
      case 'KeyD':
      case 'Tab':
        if (repeat) return true;
        this.column = this.column === 'lethal' ? 'tactical' : 'lethal';
        this._render();
        return true;
      case 'Enter':
      case 'NumpadEnter':
      case 'Space':
        if (repeat) return true;
        this._confirm();
        return true;
      case 'Escape':
      case 'Backspace':
      case TUNING.openKey:
        if (repeat) return true;
        this.close();
        return true;
      default:
        // Swallow everything else: a key that leaks to `Input` from here is a key
        // that moves the player behind the menu.
        return true;
    }
  }

  _confirm() {
    const def = this._rosterFor(this.column)[this.cursor[this.column]];
    if (!def) return;
    if (!this._unlocked(def)) {
      this.root.querySelector('.eq-panel').classList.remove('is-deny');
      requestAnimationFrame(() => this.root.querySelector('.eq-panel').classList.add('is-deny'));
      this.ctx.events.emit('audio:play', { id: 'ui_deny', volume: 0.4 });
      return;
    }
    this.selection[this.column] = def.id;
    this._save();
    this._publish();
    this.ctx.events.emit('audio:play', { id: 'ui_select', volume: 0.4 });
    this._render();
  }

  /* -------------------------------- lifecycle ------------------------------ */

  open() {
    if (this.isOpen) return false;
    this.isOpen = true;
    // Put the cursor on what is already equipped, which is almost always what the
    // player wants to compare against.
    for (const col of ['lethal', 'tactical']) {
      const i = this._rosterFor(col).findIndex((d) => d.id === this.selection[col]);
      if (i >= 0) this.cursor[col] = i;
    }
    this.root.classList.add('is-open');
    this._render();
    this.ctx.events?.emit?.('game:state', { state: 'menu' });
    this.ctx.events?.emit?.('audio:play', { id: 'ui_open', volume: 0.4 });
    return true;
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.classList.remove('is-open');
    this.ctx.events?.emit?.('audio:play', { id: 'ui_close', volume: 0.35 });
    // The caller decides what comes next: the loadout screen reopens itself, and a
    // direct open from the pause menu goes back to playing.
    if (this.onClose) this.onClose();
    else this.ctx.events?.emit?.('game:state', { state: 'playing' });
  }

  /* --------------------------------- render -------------------------------- */

  _render() {
    const lv = this.level;
    this.lvEl.textContent = String(lv);

    for (const col of ['lethal', 'tactical']) {
      const roster = this._rosterFor(col);
      const cur = this.cursor[col];
      this.lists[col].innerHTML = roster
        .map((d, i) => {
          const unlocked = this._unlocked(d);
          const equipped = this.selection[col] === d.id;
          const cls = [
            'eq-item',
            i === cur && this.column === col ? 'is-cursor' : '',
            equipped ? 'is-equipped' : '',
            unlocked ? '' : 'is-locked',
          ]
            .filter(Boolean)
            .join(' ');
          // The affordance matters more than it looks. Every row is already tappable
          // and always was, but nothing on screen said so, so a player on a phone
          // hunted for buttons that did not exist and concluded the screen was
          // broken. A tick for what is on, an explicit "tap to equip" chevron for
          // what is not, and a padlock for what cannot be.
          const label = unlocked
            ? equipped
              ? `<span class="eq-tag is-on">✓ ${t('eq.equipped')}</span>`
              : `<span class="eq-tag eq-pick">${t('eq.tap')} ›</span>`
            : `<span class="eq-tag eq-lock">🔒 LV ${d.unlock}</span>`;
          const icon = this.ctx.icons?.object?.(`eq:${d.id}`, () => buildEquipmentModel(d.id));
          return `<div class="${cls}" data-id="${d.id}" role="button" tabindex="0">
              <span class="eq-icon"${icon ? ` style="background-image:url(${icon})"` : ''}></span>
              <span class="eq-name">${esc(this._name(d))}</span>${label}
            </div>`;
        })
        .join('');
      this.root
        .querySelector(`[data-col="${col}"]`)
        .classList.toggle('is-active', this.column === col);
    }

    const def = this._rosterFor(this.column)[this.cursor[this.column]];
    this.detailEl.innerHTML = def
      ? `<div class="eq-d-name">${esc(this._name(def))}</div>
         <div class="eq-d-blurb">${esc(this._blurb(def))}</div>
         <div class="eq-d-stats">${this._stats(def)}</div>`
      : '';

    this._renderFoot();
  }

  _name(d) {
    return (getLang() === 'en' ? d.nameEn || d.name : d.name) ?? d.id;
  }

  _blurb(d) {
    return (getLang() === 'en' ? d.blurbEn || d.blurb : d.blurb) ?? '';
  }

  /**
   * The numbers, and only the ones this piece actually has.
   *
   * Printing a zero for every field an item does not use is how a stat block starts
   * lying — a smoke grenade with "DAMAGE 0" reads as a weak grenade rather than as
   * something that was never meant to hurt anyone.
   */
  _stats(d) {
    const rows = [];
    const add = (k, v) => rows.push(`<span><i>${k}</i><b>${v}</b></span>`);
    if (d.damage != null && d.effect === 'blast') add(t('eq.damage'), d.damage);
    if (d.damage != null && d.effect === 'burn') add(t('eq.dps'), `${d.damage}/s`);
    if (d.radius != null) add(t('eq.radius'), `${d.radius} m`);
    if (d.duration != null) add(t('eq.duration'), `${d.duration} s`);
    if (d.heal != null) add(t('eq.heal'), d.heal);
    if (d.fuse) add(t('eq.fuse'), `${d.fuse} s`);
    if (d.cookable) add(t('eq.cook'), '✓');
    return rows.join('');
  }

  /**
   * The footer, and the one place this screen was genuinely broken on a phone.
   *
   * It used to publish EQUIP and SWITCH LIST chips, which synthesise Enter and Tab.
   * Both act on a *cursor*, and there is no way to move a cursor without arrow keys
   * — so on a touch device the only two controls offered were the two that could not
   * do anything, and the instruction line that would have said "tap a row" had been
   * replaced by them. The screen was fully operable by tapping the rows the whole
   * time and said nothing about it.
   *
   * Touch gets a hint and a way out. Selection is direct manipulation, which is what
   * it should have been on a touchscreen from the start.
   */
  _renderFoot() {
    const hint = document.createElement('span');
    hint.className = 'eq-foot-hint';
    hint.textContent = t('eq.tapHint');
    this.footEl.replaceChildren(
      hint,
      commandBar(
        [
          { key: 'cmd.back', code: 'Escape', pad: 'B', primary: true },
          { key: 'eq.equip', code: 'Enter', pad: 'A' },
          { key: 'eq.switch', code: 'Tab', pad: 'SEL' },
        ],
        { padConnected: !!this.ctx.input?.pad?.connected }
      )
    );
  }


  dispose() {
    window.removeEventListener('keydown', this._onKey, true);
    this._offLang?.();
    this.root.remove();
  }
}

function esc(s) {
  return String(s).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );
}
